import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  Avatar,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Class as ClassIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

function AdminClasses() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    section: '',
    academic_year: '',
  });

  const BRAND = {
    a: '#667eea',
    b: '#764ba2',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    surface: 'rgba(255, 255, 255, 0.95)',
    stroke: 'rgba(255,255,255,0.18)',
    ink: '#0f172a',
    muted: '#64748b',
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data);
    } catch (err) {
      setError('Failed to fetch classes');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (classItem = null) => {
    if (classItem) {
      setEditingClass(classItem);
      setFormData({
        name: classItem.name,
        section: classItem.section || '',
        academic_year: classItem.academic_year,
      });
    } else {
      setEditingClass(null);
      setFormData({
        name: '',
        section: '',
        academic_year: new Date().getFullYear().toString(),
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClass(null);
    setFormData({ name: '', section: '', academic_year: '' });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (editingClass) {
        await axios.put(`${API_URL}/classes/${editingClass.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Class updated successfully');
      } else {
        await axios.post(`${API_URL}/classes`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Class created successfully');
      }
      handleCloseDialog();
      fetchClasses();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save class');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/classes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Class deleted successfully');
      fetchClasses();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete class');
      setTimeout(() => setError(''), 3000);
    }
  };

  const statsData = [
    { title: 'Total Classes', count: classes.length, color: BRAND.a },
    { title: 'Active Classes', count: classes.length, color: '#10b981' },
    { title: 'Academic Years', count: [...new Set(classes.map(c => c.academic_year))].length, color: '#f59e0b' },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      background: BRAND.gradient,
      py: { xs: 3, md: 4 },
      px: { xs: 2, md: 4 },
    }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{
          mb: 4,
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          boxShadow: '0 18px 70px rgba(0,0,0,0.28)',
          position: 'relative',
          overflow: 'hidden',
          border: `1px solid ${BRAND.stroke}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: BRAND.gradient,
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Avatar sx={{
                  width: 56,
                  height: 56,
                  background: BRAND.gradient,
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)'
                }}>
                  <ClassIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h3" fontWeight="800" sx={{
                  background: BRAND.gradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  Manage Classes
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                Create and manage class sections for your institution
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: BRAND.gradient,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
                }
              }}
            >
              Add New Class
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statsData.map((stat, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Card sx={{
                background: BRAND.surface,
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
                transition: 'all 0.3s ease',
                border: `1px solid ${BRAND.stroke}`,
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{
                    bgcolor: alpha(stat.color, 0.14),
                    p: 1.5,
                    borderRadius: 2,
                    display: 'inline-flex',
                    mb: 2
                  }}>
                    <ClassIcon sx={{ color: stat.color, fontSize: 32 }} />
                  </Box>
                  <Typography variant="h3" fontWeight="900" sx={{ mb: 0.5, color: BRAND.ink, letterSpacing: -0.5 }}>
                    {stat.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 700 }}>
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Alerts */}
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 3, borderRadius: 2 }}>
            {success}
          </Alert>
        )}

        {/* Classes Table */}
        <Paper sx={{
          p: { xs: 3, md: 4 },
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
          border: `1px solid ${BRAND.stroke}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{
              width: 4,
              height: 32,
              background: BRAND.gradient,
              borderRadius: 2,
              mr: 2
            }} />
            <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
              All Classes
            </Typography>
          </Box>

          <TableContainer sx={{
            borderRadius: 2,
            border: '1px solid #e2e8f0',
          }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{
                    fontWeight: 700,
                    backgroundColor: '#f8fafc',
                    color: BRAND.ink,
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    Class Name
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    backgroundColor: '#f8fafc',
                    color: BRAND.ink,
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    Section
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    backgroundColor: '#f8fafc',
                    color: BRAND.ink,
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    Academic Year
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    backgroundColor: '#f8fafc',
                    color: BRAND.ink,
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    Status
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    backgroundColor: '#f8fafc',
                    color: BRAND.ink,
                    borderBottom: '2px solid #e2e8f0'
                  }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {classes.map((classItem, index) => (
                  <TableRow
                    key={classItem.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f8fafc',
                        transform: 'scale(1.001)',
                        transition: 'all 0.2s ease',
                      },
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: BRAND.ink }}>
                      {classItem.name}
                    </TableCell>
                    <TableCell sx={{ color: BRAND.ink }}>
                      {classItem.section || '-'}
                    </TableCell>
                    <TableCell sx={{ color: BRAND.ink }}>
                      {classItem.academic_year}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="Active"
                        size="small"
                        sx={{
                          bgcolor: '#dcfce7',
                          color: '#16a34a',
                          fontWeight: 600,
                          border: 'none'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => handleOpenDialog(classItem)}
                        sx={{
                          color: BRAND.a,
                          '&:hover': { bgcolor: alpha(BRAND.a, 0.1) }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(classItem.id)}
                        sx={{
                          color: '#ef4444',
                          '&:hover': { bgcolor: alpha('#ef4444', 0.1) }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Add/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }
          }}
        >
          <DialogTitle sx={{
            background: BRAND.gradient,
            color: '#fff',
            py: 3,
            px: 4,
          }}>
            <Typography variant="h5" fontWeight="700">
              {editingClass ? 'Edit Class' : 'Add New Class'}
            </Typography>
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent sx={{ px: 4, py: 3 }}>
              <TextField
                fullWidth
                label="Class Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: BRAND.a,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: BRAND.a,
                    },
                  },
                }}
                placeholder="e.g., Grade 10, Grade 11"
              />
              <TextField
                fullWidth
                label="Section (Optional)"
                value={formData.section}
                onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: BRAND.a,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: BRAND.a,
                    },
                  },
                }}
                placeholder="e.g., A, B, C"
              />
              <TextField
                fullWidth
                label="Academic Year"
                value={formData.academic_year}
                onChange={(e) => setFormData({ ...formData, academic_year: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: BRAND.a,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: BRAND.a,
                    },
                  },
                }}
                placeholder="e.g., 2024, 2024/2025"
              />
            </DialogContent>
            <DialogActions sx={{ px: 4, py: 3, bgcolor: '#f8fafc' }}>
              <Button
                onClick={handleCloseDialog}
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: BRAND.muted,
                  '&:hover': {
                    bgcolor: alpha(BRAND.muted, 0.08),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  borderRadius: 2,
                  background: BRAND.gradient,
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #5568d3 0%, #653a8b 100%)',
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                  },
                  '&:disabled': {
                    background: '#e2e8f0',
                    color: '#94a3b8',
                  },
                }}
              >
                {loading ? 'Saving...' : editingClass ? 'Update Class' : 'Add Class'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </Box>
  );
}

export default AdminClasses;
